import { useState } from 'react'
import { format, addDays, startOfDay } from 'date-fns'
import { Calendar as CalendarIcon, Loader2 } from 'lucide-react'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface ApproveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (scheduledAt?: string) => void
  isPending: boolean
}

export function ApproveDialog({ open, onOpenChange, onConfirm, isPending }: ApproveDialogProps) {
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined)
  const minDate = addDays(startOfDay(new Date()), 1)

  const handleConfirm = () => {
    onConfirm(scheduledDate ? scheduledDate.toISOString() : undefined)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve Draft</AlertDialogTitle>
          <AlertDialogDescription>
            This draft will be enqueued for Webflow publishing. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <p className="text-sm font-medium mb-2">Schedule publish (optional)</p>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {scheduledDate ? format(scheduledDate, 'MMM dd, yyyy') : 'Pick a date...'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={scheduledDate}
                onSelect={setScheduledDate}
                disabled={(date) => date < minDate}
                aria-label="Schedule publish date"
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Approve Draft
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
